import { OnboardingAddVehicleDto, ResponseErrorDto, OnboardingStep } from '@interfaces/models';
import { useEffect, useState, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { saveVehicles } from '@api/onboarding';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from '@ui/components';
import { Routes } from '@shared/enums';

import { useVehicleForm, VehicleForm } from '../../hooks';

export const useOnboardingVehicles = () => {
  const [isCompanyWithoutVehiclesSelected, setIsCompanyWithoutVehiclesSelected] = useState(false);
  const [isVehiclesFormVisible, setIsVehiclesFormVisible] = useState(true);
  const [savedVehicles, setSavedVehicles] = useState<OnboardingAddVehicleDto[]>([]);

  const { form } = useVehicleForm();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isCompanyWithoutVehiclesSelectedCheckboxVisible = useMemo(() => {
    return savedVehicles.length === 0;
  }, [savedVehicles]);

  const checkIfRegistrationNumberIsUnique = (
    registrationNumber: string,
    originalRegistrationNumber?: string,
  ) => {
    // When user is editing vehicle, we want filter out current edited vehicle,
    // otherwise there always will be error caused by duplicated registration number
    const vehiclesWithoutCurrentEditedVehicle = savedVehicles.filter(
      (savedVehicle) => savedVehicle.registrationNumber !== originalRegistrationNumber,
    );

    const isUnique = vehiclesWithoutCurrentEditedVehicle.every(
      (vehicle) => vehicle.registrationNumber !== registrationNumber,
    );
    return isUnique;
  };

  const handleSubmit = (data: VehicleForm) => {
    setSavedVehicles([...savedVehicles, data]);
    form.reset();
  };

  const handleEditVehicle = (vehicle: VehicleForm, idx: number) => {
    const updatedVehicles = [...savedVehicles];
    updatedVehicles[idx] = vehicle;
    setSavedVehicles(updatedVehicles);
  };

  const handleRemoveVehicle = (idx: number) => {
    const updatedVehicles = [...savedVehicles];
    updatedVehicles.splice(idx, 1);
    setSavedVehicles(updatedVehicles);
  };

  const toggleCompanyWithoutVehicles = () => {
    setIsCompanyWithoutVehiclesSelected(!isCompanyWithoutVehiclesSelected);
    form.reset();
  };

  const addNextVehicle = () => {
    const isUnique = checkIfRegistrationNumberIsUnique(form.getValues('registrationNumber'));

    if (!isUnique) {
      return form.setError('registrationNumber', {
        message: t('ONBOARDING.ADD_VEHICLES.REGISTRATION_NUMBER_NOT_UNIQUE'),
      });
    }

    // submit form only if form is visible. We don't wont to trigger form when user has no vehicles
    if (isVehiclesFormVisible) {
      form.handleSubmit(handleSubmit)();
    }

    setIsVehiclesFormVisible(true);
  };

  const saveVehiclesMutation = useMutation({
    mutationFn: (vehicles: OnboardingAddVehicleDto[]) => {
      return saveVehicles(vehicles, OnboardingStep.ADD_EMPLOYEES);
    },
    onSuccess: () => {
      navigate(`../${Routes.ADD_EMPLOYEES}`);
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const closeVehiclesForm = () => {
    setIsVehiclesFormVisible(false);
    form.reset();
  };

  const handleSaveAndContinue = async () => {
    // If user has selected "Company without vehicles" option, we need to save empty array
    if (isCompanyWithoutVehiclesSelected) {
      saveVehiclesMutation.mutate([]);
      return;
    }

    // Check if registration number is unique
    if (!checkIfRegistrationNumberIsUnique(form.getValues('registrationNumber'))) {
      form.setError('registrationNumber', {
        message: t('ONBOARDING.ADD_VEHICLES.REGISTRATION_NUMBER_NOT_UNIQUE'),
      });
      return;
    }

    // Check if form has any errors
    if (isVehiclesFormVisible) {
      await form.trigger();
      const hasAnyFormErrors = Object.keys(form.formState.errors).length > 0;
      if (hasAnyFormErrors) return;
    }

    // Some of vehicles are not saved to state yet, so we need to add them to the vehicles array
    // but only if form is visible
    const allVehicles = [...savedVehicles, ...(isVehiclesFormVisible ? [form.getValues()] : [])];
    saveVehiclesMutation.mutate(allVehicles);
  };

  useEffect(() => {
    if (!savedVehicles.length) {
      setIsVehiclesFormVisible(true);
    }
  }, [savedVehicles]);

  return {
    isSaveMutationPending: saveVehiclesMutation.isPending,
    isCompanyWithoutVehiclesSelectedCheckboxVisible,
    isCompanyWithoutVehiclesSelected,
    isVehiclesFormVisible,
    savedVehicles,
    form,
    checkIfRegistrationNumberIsUnique,
    toggleCompanyWithoutVehicles,
    handleSaveAndContinue,
    handleRemoveVehicle,
    closeVehiclesForm,
    handleEditVehicle,
    addNextVehicle,
    handleSubmit,
  };
};
